import { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import FleetStatusPanel from './components/FleetStatusPanel';
import ThreatWatchPanel from './components/ThreatWatchPanel';
import GoldenListHealthPanel from './components/GoldenListHealthPanel';
import ThreatAssessmentMatrix from './components/ThreatAssessmentMatrix';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const WarRoomDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [emergencyPauseActive, setEmergencyPauseActive] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const [fleetData, setFleetData] = useState([
    { id: 'srv-prod-01', name: 'Production-01', purpose: 'Production', status: 'Online', reputation: 95, ip: '192.168.1.101' },
    { id: 'srv-prod-02', name: 'Production-02', purpose: 'Production', status: 'Online', reputation: 92, ip: '192.168.1.102' },
    { id: 'srv-prod-03', name: 'Production-03', purpose: 'Production', status: 'Online', reputation: 88, ip: '192.168.1.103' },
    { id: 'srv-canary-01', name: 'Canary-01', purpose: 'Canary', status: 'Online', reputation: 78, ip: '192.168.1.201' },
    { id: 'srv-canary-02', name: 'Canary-02', purpose: 'Canary', status: 'Warning', reputation: 65, ip: '192.168.1.202' },
    { id: 'srv-quarantine-01', name: 'Quarantine-01', purpose: 'Quarantine', status: 'Quarantined', reputation: 45, ip: '192.168.1.301' },
    { id: 'srv-sanitizer-01', name: 'Sanitizer-01', purpose: 'Sanitizer', status: 'Online', reputation: 52, ip: '192.168.1.401' },
    { id: 'srv-sanitizer-02', name: 'Sanitizer-02', purpose: 'Sanitizer', status: 'Online', reputation: 48, ip: '192.168.1.402' }
  ]);

  const [threatEvents, setThreatEvents] = useState([
    {
      id: 'threat-001',
      timestamp: new Date(Date.now() - 180000),
      severity: 'Critical',
      type: 'Policy Block',
      message: 'Policy block received from Gmail for srv-prod-03',
      serverId: 'srv-prod-03',
      action: 'Server auto-quarantined'
    },
    {
      id: 'threat-002',
      timestamp: new Date(Date.now() - 360000),
      severity: 'Warning',
      type: 'Blacklist Addition',
      message: 'Server srv-canary-02 added to Spamhaus ZEN',
      serverId: 'srv-canary-02',
      action: 'Monitoring increased'
    },
    {
      id: 'threat-003',
      timestamp: new Date(Date.now() - 540000),
      severity: 'Warning',
      type: 'Reputation Drop',
      message: 'Reputation dropped 15 points on srv-canary-01',
      serverId: 'srv-canary-01',
      action: 'Daily limit reduced'
    },
    {
      id: 'threat-004',
      timestamp: new Date(Date.now() - 720000),
      severity: 'Info',
      type: 'Canary Simulation Failed',
      message: 'Campaign "Flash Sale" failed canary test - complaint rate 0.15%',
      serverId: 'srv-canary-01',
      action: 'Campaign paused'
    },
    {
      id: 'threat-005',
      timestamp: new Date(Date.now() - 900000),
      severity: 'Success',
      type: 'Rehabilitation Complete',
      message: 'Server srv-prod-02 rehabilitation completed successfully',
      serverId: 'srv-prod-02',
      action: 'Promoted to Production'
    }
  ]);

  const [goldenListData, setGoldenListData] = useState({
    totalContacts: 45230,
    tiers: {
      platinum: { count: 8920, percentage: 19.7, engagementScore: 95 },
      gold: { count: 15420, percentage: 34.1, engagementScore: 88 },
      silver: { count: 12340, percentage: 27.3, engagementScore: 72 },
      bronze: { count: 6780, percentage: 15.0, engagementScore: 58 },
      lead: { count: 1770, percentage: 3.9, engagementScore: 35 }
    },
    avgEngagement: 78.4,
    listQuality: 92
  });

  const [systemRiskLevel, setSystemRiskLevel] = useState({
    overall: 35,
    production: 15,
    canary: 55,
    quarantine: 85,
    sanitizer: 45
  });

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        const newEvent = {
          id: `threat-${Date.now()}`,
          timestamp: new Date(),
          severity: ['Info', 'Success', 'Warning']?.[Math.floor(Math.random() * 3)],
          type: ['Reputation Change', 'Health Check', 'Campaign Update']?.[Math.floor(Math.random() * 3)],
          message: 'System health check completed successfully',
          serverId: fleetData?.[Math.floor(Math.random() * fleetData?.length)]?.id,
          action: 'No action required'
        };
        setThreatEvents(prev => [newEvent, ...prev]?.slice(0, 20));
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, fleetData]);

  const handleEmergencyPause = () => {
    setEmergencyPauseActive(!emergencyPauseActive);
  };

  return (
    <div className="min-h-screen bg-slate-950 dark">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main 
        className={`pt-20 transition-all duration-300 ${
          sidebarCollapsed ? 'lg:pl-[80px]' : 'lg:pl-[280px]'
        }`}
      >
        <div className="p-4 md:p-6 lg:p-8">
          <div className="mb-6 md:mb-8">
            <Breadcrumbs />
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-semibold text-white mb-2">
                  War Room Dashboard
                </h1>
                <p className="text-base md:text-lg text-slate-400">
                  Mission-critical fleet monitoring and threat response
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant={autoRefresh ? 'default' : 'outline'}
                  size="sm"
                  iconName={autoRefresh ? 'Pause' : 'Play'}
                  onClick={() => setAutoRefresh(!autoRefresh)}
                >
                  {autoRefresh ? 'Auto-Refresh' : 'Paused'}
                </Button>
                <Button
                  variant={emergencyPauseActive ? 'danger' : 'warning'}
                  size="sm"
                  iconName="AlertTriangle"
                  onClick={handleEmergencyPause}
                >
                  {emergencyPauseActive ? 'Resume Fleet' : 'Emergency Pause'}
                </Button>
              </div>
            </div>
          </div>

          {emergencyPauseActive && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-500/50 rounded-lg flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} className="text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-red-400 font-semibold mb-1">Emergency Fleet Pause Active</div>
                <div className="text-red-300 text-sm">All outbound email sending has been paused. Click "Resume Fleet" to restore operations.</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
            <div className="xl:col-span-2">
              <FleetStatusPanel servers={fleetData} emergencyPause={emergencyPauseActive} />
            </div>
            <div>
              <GoldenListHealthPanel data={goldenListData} />
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <ThreatWatchPanel events={threatEvents} autoRefresh={autoRefresh} />
            </div>
            <div>
              <ThreatAssessmentMatrix riskLevels={systemRiskLevel} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WarRoomDashboard;